import dayjs from "./dayjsConfig";
import { promptBiometric } from "./biometric";
import {
  persistRefreshedBiometricCredential,
  ValidBiometricCredential,
} from "./biometricSession";
import { AppDispatch } from "../store/store";
import {
  biometricsLoggedIn,
  refreshTokenWithValue,
  setBiometricInProgress,
} from "../store/slices/authSlice";
import { AuthData } from "../types/types";

type BiometricReauthParams = {
  dispatch: AppDispatch;
  credential: ValidBiometricCredential;
  userEmail?: string | null;
  /**
   * Recompute server diff using refreshed execute_at rather than using stored diff.
   * Useful when quick login wants to resync against server time.
   */
  recomputeServerDiff?: boolean;
};

type BiometricReauthSuccess = {
  success: true;
  refreshedAuth: AuthData;
  usernameUsed: string;
  serverDiffMs: number;
};

type BiometricReauthFailure = {
  success: false;
  step: "prompt" | "refresh" | "missing-data" | "username" | "persist" | "timeout";
  error: string;
  // Raw native error code from the biometric plugin (only set when step === "prompt").
  // Useful to distinguish real biometric failures from OS-level cancellations in
  // telemetry, since the mapped enum collapses several native codes.
  errorCode?: string;
  nativeMessage?: string;
};

export type BiometricReauthResult =
  | BiometricReauthSuccess
  | BiometricReauthFailure;

let reauthInFlight: Promise<BiometricReauthResult> | null = null;

// Safety timeout: if the entire reauth flow (prompt + network + persist) takes
// longer than this, we bail out so the user is never stuck on a blank screen.
const REAUTH_TIMEOUT_MS = 30_000;

/**
 * Shared biometric re-authentication flow:
 * - Prompt biometric
 * - Refresh token using secure refresh_token
 * - Persist updated credential (refresh token + metadata)
 * - Mark biometrics as logged in
 */
export const performBiometricReauth = async ({
  dispatch,
  credential,
  userEmail,
  recomputeServerDiff = false,
}: BiometricReauthParams): Promise<BiometricReauthResult> => {
  if (reauthInFlight) {
    return reauthInFlight;
  }

  const runReauth = async (): Promise<BiometricReauthResult> => {
    dispatch(setBiometricInProgress(true));

    try {
      await promptBiometric();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const typed = error as { code?: string; nativeMessage?: string };
      dispatch(setBiometricInProgress(false));
      return {
        success: false,
        step: "prompt",
        error: errorMessage,
        errorCode: typed.code,
        nativeMessage: typed.nativeMessage,
      };
    }

    const refreshedAction = await dispatch(
      refreshTokenWithValue({
        refreshToken: credential.refreshToken,
        state: credential.state ?? null,
      }),
    );

    if (refreshedAction.meta.requestStatus === "rejected") {
      const errorMessage =
        (refreshedAction.payload as string | undefined) ||
        "Biometric refresh failed";
      dispatch(setBiometricInProgress(false));
      return { success: false, step: "refresh", error: errorMessage };
    }

    const refreshed = refreshedAction.payload as AuthData | undefined;

    if (
      !refreshed?.access_token ||
      !refreshed?.refresh_token ||
      !refreshed?.execute_at
    ) {
      dispatch(setBiometricInProgress(false));
      return {
        success: false,
        step: "missing-data",
        error: "Biometric refresh returned incomplete token data",
      };
    }

    const username = credential.username || userEmail || null;

    if (!username) {
      dispatch(setBiometricInProgress(false));
      return {
        success: false,
        step: "username",
        error: "Missing username to persist biometric credential",
      };
    }

    const serverDiffMs = recomputeServerDiff
      ? dayjs().utc().diff(dayjs.utc(refreshed.execute_at), "millisecond")
      : credential.serverDiffMs;

    try {
      await persistRefreshedBiometricCredential({
        username,
        refreshToken: refreshed.refresh_token,
        serverDiffMs,
        executeAt: refreshed.execute_at,
        existingExpiresAt: credential.refreshExpiresAt,
        state: refreshed.state ?? credential.state ?? null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch(setBiometricInProgress(false));
      return { success: false, step: "persist", error: errorMessage };
    }

    dispatch(biometricsLoggedIn(true));
    dispatch(setBiometricInProgress(false));

    return {
      success: true,
      refreshedAuth: refreshed,
      usernameUsed: username,
      serverDiffMs,
    };
  };

  const withTimeout = Promise.race([
    runReauth(),
    new Promise<BiometricReauthFailure>((resolve) =>
      setTimeout(
        () => resolve({ success: false, step: "timeout", error: "Biometric reauth timed out" }),
        REAUTH_TIMEOUT_MS,
      ),
    ),
  ]).then((result) => {
    // If the timeout won, make sure we clear the in-progress flag
    if (!result.success && result.step === "timeout") {
      dispatch(setBiometricInProgress(false));
    }
    return result;
  });

  reauthInFlight = withTimeout;
  try {
    return await reauthInFlight;
  } finally {
    reauthInFlight = null;
  }
};
